import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Link } from 'react-router-dom';
import {
  Users, UserCheck, Clock, FileCheck, CheckCircle, XCircle, DollarSign, Hourglass,
  Plus, Upload, BarChart3, CalendarPlus, Bell, Search, Activity, ArrowRight, StickyNote
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const deltaColorClass = (delta) => {
  if (delta > 0) return 'text-emerald-600';
  if (delta < 0) return 'text-rose-600';
  return 'text-muted-foreground';
};

const deltaSymbol = (delta) => {
  if (delta > 0) return '▲';
  if (delta < 0) return '▼';
  return '●';
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [leads, setLeads] = useState([]);
  const [userWise, setUserWise] = useState([]);
  const [branchWise, setBranchWise] = useState([]);
  const [dailyLeads, setDailyLeads] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentNotes, setRecentNotes] = useState([]);
  const [fupSummary, setFupSummary] = useState({ today: 0, pending: 0, overdue: 0, completed: 0, by_employee: [], by_branch: [] });
  const [loading, setLoading] = useState(true);
  // --- Phase 13: Period filter ---
  const [period, setPeriod] = useState('current_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [periodData, setPeriodData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        api.get('/dashboard/stats'),
        api.get('/followups', { params: { status: 'pending' } }),
        api.get('/leads'),
        api.get('/dashboard/daily-leads', { params: { days: 7 } }),
      ];
      // Add admin-only requests
      if (['super_admin', 'admin', 'team_leader'].includes(user?.role)) {
        requests.push(api.get('/dashboard/user-wise'));
        requests.push(api.get('/dashboard/branch-wise'));
      } else {
        requests.push(Promise.resolve({ data: [] }));
        requests.push(Promise.resolve({ data: [] }));
      }
      // Always fetch latest activities for the Recent Activities widget
      requests.push(api.get('/activities', { params: { entity_type: 'lead' } }).catch(() => ({ data: [] })));
      // Recent notes
      requests.push(api.get('/notes', { params: { limit: 6 } }).catch(() => ({ data: [] })));
      // Follow-up dashboard summary
      requests.push(api.get('/dashboard/followups').catch(() => ({ data: { today: 0, pending: 0, overdue: 0, completed: 0, by_employee: [], by_branch: [] } })));
      // Phase 13: period-filtered dashboard
      const periodParams = { period };
      if (period === 'custom' && customStart && customEnd) {
        periodParams.start = customStart;
        periodParams.end = customEnd;
      }
      requests.push(api.get('/dashboard/period', { params: periodParams }).catch(() => ({ data: null })));
      const responses = await Promise.all(requests.map((r) => r.catch ? r.catch(() => ({ data: [] })) : r));
      setStats(responses[0].data);
      setFollowups(responses[1].data.slice(0, 4));
      setLeads(responses[2].data);
      setDailyLeads(responses[3].data || []);
      setUserWise((responses[4].data || []).slice(0, 5));
      setBranchWise((responses[5].data || []).slice(0, 5));
      setRecentActivities((responses[6].data || []).slice(0, 8));
      setRecentNotes((responses[7].data || []).slice(0, 6));
      setFupSummary(responses[8].data || {});
      const periodPayload = responses[9].data || null;
      setPeriodData(periodPayload);
      // If period data is available, prefer it for Recent Notes + Recent Activities so
      // both widgets respect the selected reporting period
      if (periodPayload) {
        setRecentActivities((periodPayload.recent_activities || []).slice(0, 8));
        setRecentNotes((periodPayload.recent_notes || []).slice(0, 6));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role, period, customStart, customEnd]);

  useEffect(() => {
    fetchData();
    // Phase 13: Poll every 60 seconds per spec
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Period-aware KPIs (fall back to legacy stats if endpoint not yet loaded)
  const kpi = periodData?.kpis || {};
  const pk = (name, legacy) => kpi[name]?.value ?? legacy ?? 0;
  const pd = (name) => kpi[name]?.delta_pct;

  // Compute pending leads (not in final states)
  const pendingCases = pk('pending_leads', stats?.pending_leads);
  // Compute conversion ratio
  const totalPeriodLeads = pk('total_leads', stats?.total_leads);
  const conversionRatio = totalPeriodLeads > 0
    ? Math.round((pk('disbursed_leads', stats?.disbursed_cases) / totalPeriodLeads) * 100)
    : 0;

  const statCards = [
    { label: "Today's Leads", value: pk('today_leads', stats?.today_leads), delta: pd('today_leads'), icon: UserCheck, color: 'bg-sky-500', testId: 'stat-today-leads' },
    { label: 'Pending Leads', value: pendingCases, delta: pd('pending_leads'), icon: Hourglass, color: 'bg-amber-500', testId: 'stat-pending-leads' },
    { label: 'Approved Leads', value: pk('approved_leads', stats?.approved_leads), delta: pd('approved_leads'), icon: CheckCircle, color: 'bg-emerald-500', testId: 'stat-approved-leads' },
    { label: 'Rejected Leads', value: pk('rejected_leads', stats?.rejected_cases), delta: pd('rejected_leads'), icon: XCircle, color: 'bg-rose-500', testId: 'stat-rejected-leads' },
    { label: 'Disbursed Leads', value: pk('disbursed_leads', stats?.disbursed_cases), delta: pd('disbursed_leads'), icon: DollarSign, color: 'bg-teal-500', testId: 'stat-disbursed-leads' },
    { label: 'Total Leads', value: totalPeriodLeads, delta: pd('total_leads'), icon: Users, color: 'bg-blue-600', testId: 'stat-total-leads' },
    { label: 'Fresh Leads', value: pk('fresh_leads', stats?.fresh_leads), delta: pd('fresh_leads'), icon: FileCheck, color: 'bg-violet-500', testId: 'stat-fresh-leads' },
    { label: 'Follow-ups', value: pk('total_followups', (stats?.follow_up_leads || 0) + (stats?.pending_followups || 0)), delta: pd('total_followups'), icon: Clock, color: 'bg-cyan-500', testId: 'stat-followup-leads' },
    { label: 'Notes Today', value: pk('today_notes', stats?.notes_added_today), delta: pd('today_notes'), icon: StickyNote, color: 'bg-fuchsia-500', testId: 'stat-notes-today' },
  ];

  // Use period-filtered daily series when available; else fall back to legacy /daily-leads
  let chartData = [];
  if (periodData?.daily_series?.length) {
    chartData = periodData.daily_series.map((d) => ({ day: d.date, leads: d.count, logins: 0, disbursed: 0 }));
  } else if (dailyLeads.length > 0) {
    chartData = dailyLeads.map((d) => ({ day: d.date, leads: d.leads, logins: Math.round(d.leads * 0.3), disbursed: Math.round(d.leads * 0.1) }));
  }

  // Pie chart data
  const pieData = [
    { name: 'Fresh', value: stats?.fresh_leads || 0, color: '#3B82F6' },
    { name: 'Contacted', value: stats?.contacted_leads || 0, color: '#6366F1' },
    { name: 'Interested', value: stats?.interested_leads || 0, color: '#06B6D4' },
    { name: 'Docs Pending', value: stats?.documents_pending || 0, color: '#EAB308' },
    { name: 'Follow-up', value: stats?.follow_up_leads || 0, color: '#F97316' },
    { name: 'Login', value: stats?.login_cases || 0, color: '#8B5CF6' },
    { name: 'Under Process', value: stats?.under_process || 0, color: '#A855F7' },
    { name: 'Sanctioned', value: stats?.sanctioned_cases || 0, color: '#10B981' },
    { name: 'Disbursed', value: stats?.disbursed_cases || 0, color: '#14B8A6' },
    { name: 'Rejected', value: stats?.rejected_cases || 0, color: '#F43F5E' },
  ].filter((d) => d.value > 0);

  const totalLeadsForPercent = stats?.total_leads || 1;

  const quickActions = [
    { label: 'Add New Lead', icon: Plus, to: '/leads/new', color: 'bg-blue-50 text-blue-600', testId: 'quick-add-lead' },
    { label: 'Add Follow-up', icon: CalendarPlus, to: '/followups', color: 'bg-cyan-50 text-cyan-600', testId: 'quick-followup' },
    { label: 'Upload Document', icon: Upload, to: '/leads', color: 'bg-sky-50 text-sky-600', testId: 'quick-upload' },
    { label: 'View Reports', icon: BarChart3, to: '/reports', color: 'bg-violet-50 text-violet-600', testId: 'quick-reports' },
  ];

  const getInitials = (name) => name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const getLeadName = (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    return lead ? lead.customer_name : 'Unknown';
  };

  const getLeadType = (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    return lead ? `${lead.loan_type.charAt(0).toUpperCase() + lead.loan_type.slice(1)} Loan` : '';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Welcome back, {user?.full_name}!</h1>
                <p className="text-muted-foreground text-sm mt-1">
                  Reporting Period: <span className="font-semibold text-foreground">{periodData?.period_label || 'Current Month'}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  data-testid="dashboard-period-filter"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-card text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                >
                  <option value="current_month">Current Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="last_3_months">Last 3 Months</option>
                  <option value="last_6_months">Last 6 Months</option>
                  <option value="this_year">This Year</option>
                  <option value="previous_year">Previous Year</option>
                  <option value="custom">Custom Range…</option>
                </select>
                {period === 'custom' && (
                  <>
                    <input type="date" data-testid="period-start" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="px-2 py-2 rounded-lg border border-border bg-card text-sm" />
                    <input type="date" data-testid="period-end" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="px-2 py-2 rounded-lg border border-border bg-card text-sm" />
                  </>
                )}
                <button className="relative p-2 rounded-lg bg-card border border-border hover:bg-muted">
                  <Bell size={18} className="text-foreground" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{followups.length}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Stat cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.label}
                  data-testid={card.testId}
                  className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-all"
                >
                  <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-3 shadow-sm`}>
                    <Icon className="text-white" size={22} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">{card.value}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{card.label}</p>
                  {card.delta !== null && card.delta !== undefined && (
                    <p
                      data-testid={`${card.testId}-delta`}
                      className={`text-[11px] mt-1 font-medium ${deltaColorClass(card.delta)}`}
                    >
                      {deltaSymbol(card.delta)} {Math.abs(card.delta)}% vs previous
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Business Overview</h3>
                <select className="text-xs px-3 py-1 rounded-lg border border-border bg-background">
                  <option>This Month</option>
                  <option>Last Month</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="leadsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2D9CFF" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#2D9CFF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="loginsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="day" stroke="#9CA3AF" fontSize={12} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip />
                  <Area type="monotone" dataKey="leads" stroke="#2D9CFF" fill="url(#leadsGradient)" strokeWidth={2} />
                  <Area type="monotone" dataKey="logins" stroke="#06B6D4" fill="url(#loginsGradient)" strokeWidth={2} />
                  <Area type="monotone" dataKey="disbursed" stroke="#10B981" fill="none" strokeWidth={2} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Lead Status Distribution</h3>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData.length > 0 ? pieData : [{ name: 'No data', value: 1, color: '#E5E7EB' }]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {(pieData.length > 0 ? pieData : [{ color: '#E5E7EB' }]).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  <div className="text-center mb-3">
                    <p className="text-2xl font-bold text-foreground">{stats?.total_leads || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Leads</p>
                  </div>
                  {pieData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="text-foreground">{item.name}</span>
                      </div>
                      <span className="text-muted-foreground font-medium">
                        {Math.round((item.value / totalLeadsForPercent) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* User-wise & Branch-wise stats */}
          {(['super_admin', 'admin', 'team_leader'].includes(user?.role)) && (userWise.length > 0 || branchWise.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-card border border-border rounded-xl p-6" data-testid="user-wise-performance">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">User-wise Performance</h3>
                  <Link to="/users" className="text-xs text-primary hover:underline">View All</Link>
                </div>
                <div className="space-y-3">
                  {userWise.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No data available</p>
                  ) : (
                    userWise.map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                            {getInitials(u.name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{u.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{u.role.replace('_', ' ')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground">{u.total_leads}</p>
                          <p className="text-xs text-emerald-600">{u.disbursed} disbursed</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Branch Performance</h3>
                  <Link to="/branches" className="text-xs text-primary hover:underline">View All</Link>
                </div>
                <div className="space-y-3">
                  {branchWise.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No branches yet</p>
                  ) : (
                    branchWise.map((b) => {
                      const maxLeads = Math.max(...branchWise.map((x) => x.total_leads), 1);
                      const pct = (b.total_leads / maxLeads) * 100;
                      return (
                        <div key={b.branch_id}>
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-sm font-medium text-foreground">{b.name} <span className="text-xs text-muted-foreground">({b.code})</span></p>
                            <p className="text-sm text-foreground"><span className="font-bold">{b.total_leads}</span> <span className="text-xs text-muted-foreground">leads</span></p>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Conversion Ratio Card */}
          <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Lead Conversion Ratio</p>
                <h3 className="text-4xl font-bold text-foreground">{conversionRatio}%</h3>
                <p className="text-xs text-muted-foreground mt-1">Disbursed / Total Leads</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground mb-1">Today's New Leads</p>
                <h3 className="text-4xl font-bold text-primary">{stats?.today_leads || 0}</h3>
                <p className="text-xs text-muted-foreground mt-1">Created today</p>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-80 space-y-6">
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="space-y-2">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.label}
                    to={action.to}
                    data-testid={action.testId}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center`}>
                      <Icon size={18} />
                    </div>
                    <span className="text-sm font-medium text-foreground">{action.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-5" data-testid="recent-notes-widget">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <StickyNote size={18} className="text-primary" /> Recent Notes
              </h3>
              <Link to="/leads" className="text-xs text-primary hover:underline">View Leads</Link>
            </div>
            <div className="space-y-3">
              {recentNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
              ) : (
                recentNotes.map((n) => (
                  <Link
                    to={`/leads/${n.lead_id}`}
                    key={n.id}
                    className="block border-l-2 border-fuchsia-400/50 pl-3 py-1 hover:bg-accent/40 rounded-r transition-colors"
                    data-testid={`recent-note-${n.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{n.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{n.content}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {n.lead_number && <span className="font-mono mr-1">{n.lead_number}</span>}
                          {n.customer_name && <span>· {n.customer_name}</span>}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          <span className="text-foreground/80 font-medium">{n.actor_name || 'User'}</span>
                          {n.actor_role && <span className="ml-1">· {n.actor_role.replace(/_/g, ' ')}</span>}
                        </p>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        n.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                        n.priority === 'low' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {n.priority?.toUpperCase()}
                      </span>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-5" data-testid="recent-activities-widget">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Activity size={18} className="text-primary" /> Recent Activities
              </h3>
              <Link to="/activities" className="text-xs text-primary hover:underline">View All</Link>
            </div>
            <div className="space-y-3">
              {recentActivities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
              ) : (
                recentActivities.map((a) => (
                  <div key={a.id} className="border-l-2 border-primary/30 pl-3 py-1" data-testid={`recent-activity-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {a.lead_number && <span className="font-mono mr-1">{a.lead_number}</span>}
                          {a.customer_name && <span>· {a.customer_name}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{a.action.replace(/_/g, ' ')}</p>
                        {(a.previous_status || a.new_status) && (
                          <div className="flex items-center gap-1 text-[10px] mt-0.5">
                            <span className="text-muted-foreground">{a.previous_status || '—'}</span>
                            <ArrowRight size={10} className="text-muted-foreground" />
                            <span className="text-primary capitalize">{a.new_status || '—'}</span>
                          </div>
                        )}
                        <p className="text-[11px] text-muted-foreground mt-1 truncate">
                          {a.actor_name || 'System'}
                          {a.actor_role && <span className="ml-1">· {a.actor_role.replace(/_/g, ' ')}</span>}
                          {a.actor_employee_id && <span className="ml-1 font-mono">#{a.actor_employee_id}</span>}
                        </p>
                      </div>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {new Date(a.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-5" data-testid="followup-summary-widget">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <CalendarPlus size={18} className="text-primary" /> Follow-up Summary
              </h3>
              <Link to="/followups" className="text-xs text-primary hover:underline">Manage</Link>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg bg-amber-50 px-3 py-2" data-testid="fup-today">
                <p className="text-[10px] uppercase tracking-wide text-amber-700">Today</p>
                <p className="text-xl font-bold text-amber-700">{fupSummary.today || 0}</p>
              </div>
              <div className="rounded-lg bg-blue-50 px-3 py-2" data-testid="fup-pending">
                <p className="text-[10px] uppercase tracking-wide text-blue-700">Pending</p>
                <p className="text-xl font-bold text-blue-700">{fupSummary.pending || 0}</p>
              </div>
              <div className="rounded-lg bg-rose-50 px-3 py-2" data-testid="fup-overdue">
                <p className="text-[10px] uppercase tracking-wide text-rose-700">Overdue</p>
                <p className="text-xl font-bold text-rose-700">{fupSummary.overdue || 0}</p>
              </div>
              <div className="rounded-lg bg-emerald-50 px-3 py-2" data-testid="fup-completed">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700">Completed</p>
                <p className="text-xl font-bold text-emerald-700">{fupSummary.completed || 0}</p>
              </div>
            </div>
            {(fupSummary.by_employee || []).length > 0 && (
              <div className="mt-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">By Employee</p>
                {(fupSummary.by_employee || []).slice(0, 3).map((e) => (
                  <div key={e.user_id} className="flex items-center justify-between text-xs py-1">
                    <span className="text-foreground/85 truncate">{e.user_name || 'Unknown'}</span>
                    <span className="text-muted-foreground">{e.pending}/{e.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Today's Follow-ups</h3>
              <Link to="/followups" className="text-xs text-primary hover:underline">View All</Link>
            </div>
            <div className="space-y-3">
              {followups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No follow-ups scheduled</p>
              ) : (
                followups.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                      {getInitials(getLeadName(f.lead_id))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{getLeadName(f.lead_id)}</p>
                      <p className="text-xs text-muted-foreground">{getLeadType(f.lead_id)}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{f.follow_up_time}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground text-center md:text-left">
        © 2026 Goldpeak Financial Solutions Pvt. Ltd. All rights reserved. <span className="italic ml-2">We are with you</span>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
